import { Eye, MoveHorizontal, MoveVertical } from 'lucide-react';
import type { ReactNode } from 'react';
import type { MenuItem } from '../Menu';
import type { TreeOrientation } from './TreeChart';

/**
 * The entries every tree surface's settings cog opens with: how the chart is drawn, then
 * which records it draws.
 *
 * Shared so the two chart surfaces cannot word or order these differently. A caller appends
 * its own entries — History Logs, Clear data — after them.
 *
 * **A tick, not a checkbox.** These are the index shell's cog semantics: the set choice
 * carries a glyph and the others carry a spacer of the same size, so the column of labels
 * stays aligned whichever one is set.
 */
export function treeSettingsItems({
  orientation,
  onOrientation,
  includeDeleted,
  onIncludeDeleted,
}: {
  orientation: TreeOrientation;
  onOrientation: (next: TreeOrientation) => void;
  includeDeleted: boolean;
  onIncludeDeleted: (next: boolean) => void;
}): MenuItem[] {
  return [
    /*
      Named for what they draw. The root sits on the left in one and on the top in the other,
      so "Left to right" is what the reader sees — a literal "Right to left" would be a
      mirrored chart with the root on the right edge, which is a different thing entirely.
    */
    {
      key: 'orientation-horizontal',
      label: 'Left to right',
      icon: glyph(orientation === 'horizontal', <MoveHorizontal className="h-4 w-4" aria-hidden="true" />),
      onClick: () => onOrientation('horizontal'),
    },
    {
      key: 'orientation-vertical',
      label: 'Top to bottom',
      icon: glyph(orientation === 'vertical', <MoveVertical className="h-4 w-4" aria-hidden="true" />),
      onClick: () => onOrientation('vertical'),
    },
    /*
      Show deleted lives here, with every other filter, rather than on the page beside the
      chart — one place to look for whatever is narrowing the list, which is the index shell's
      arrangement too.

      Deliberately NOT persisted, unlike the orientation above it. This one changes which
      records are on screen, and a view that quietly kept showing yesterday's deleted units is
      how a deleted unit gets treated as live.
    */
    {
      key: 'show-deleted',
      label: 'Show deleted',
      icon: glyph(includeDeleted, <Eye className="h-4 w-4" aria-hidden="true" />),
      divider: true,
      onClick: () => onIncludeDeleted(!includeDeleted),
    },
  ];
}


/** The set glyph, or a spacer of the same size so the labels stay in one column. */
const glyph = (on: boolean, icon: ReactNode): ReactNode =>
  on ? icon : <span className="h-4 w-4 shrink-0" aria-hidden="true" />;
